import type {
  AssetAmount,
  BalanceResponse,
  ChainTarget,
  ClaimRequest,
  ClaimResponse,
  GoogleKeyResponse,
  PendingClaimError,
  PublicConfig,
  RelayFeeTopUpError,
} from "./types";

const backendBaseUrl = (import.meta.env.VITE_BACKEND_API_URL?.trim() || "/api").replace(/\/$/, "");

export class ApiRequestError extends Error {
  code?: string;
  relayFeeTopUp?: RelayFeeTopUpError;
  pendingClaim?: PendingClaimError;
}

export async function fetchConfig(): Promise<PublicConfig> {
  return requestJson<PublicConfig>("/v1/config");
}

export async function fetchGoogleKey(kid: string): Promise<GoogleKeyResponse> {
  return requestJson<GoogleKeyResponse>(`/v1/google-key/${encodeURIComponent(kid)}`);
}

export async function fetchBalance(chain: ChainTarget, email: string): Promise<BalanceResponse> {
  return requestJson<BalanceResponse>("/v1/balance", {
    method: "POST",
    body: JSON.stringify({ chain, email }),
  });
}

export async function fetchWalletAssets(
  chain: ChainTarget,
  address: string,
): Promise<AssetAmount[]> {
  return requestJson<AssetAmount[]>("/v1/wallet-assets", {
    method: "POST",
    body: JSON.stringify({ chain, address }),
  });
}

export async function estimateRelayFeeBudget(
  chain: ChainTarget,
  asset: string,
  amount: string,
): Promise<AssetAmount> {
  return requestJson<AssetAmount>("/v1/fee-estimate", {
    method: "POST",
    body: JSON.stringify({ chain, asset, amount }),
  });
}

export async function buildSendAction(request: {
  chain: ChainTarget;
  from: string;
  toEmail: string;
  asset: string;
  amount: string;
  timeout: number;
}): Promise<{ to: string; value: string; data: string }> {
  return requestJson("/v1/send", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function submitClaim(request: ClaimRequest): Promise<ClaimResponse> {
  return requestJson<ClaimResponse>("/v1/claim", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function sendNotification(
  chain: ChainTarget,
  txHash: string,
  toEmail: string,
): Promise<void> {
  await requestJsonOrEmpty("/v1/send-notification", {
    method: "POST",
    body: JSON.stringify({ chain, txHash, toEmail }),
  });
}

async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await request(path, init);
  return (await response.json()) as T;
}

async function requestJsonOrEmpty(path: string, init: RequestInit = {}): Promise<void> {
  await request(path, init);
}

async function request(path: string, init: RequestInit): Promise<Response> {
  const response = await fetch(`${backendBaseUrl}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...init.headers,
    },
  });

  if (!response.ok) {
    throw await apiError(response);
  }

  return response;
}

async function apiError(response: Response): Promise<ApiRequestError> {
  const fallback = `${response.status} ${response.statusText}`;
  const bodyText = await response.text();
  const error = new ApiRequestError(fallback);
  try {
    const body = JSON.parse(bodyText) as {
      error?: {
        code?: string;
        message?: string;
        details?: {
          relayFeeTopUp?: RelayFeeTopUpError;
          pendingClaim?: PendingClaimError;
        };
      };
    };
    if (body.error?.code !== undefined) {
      error.code = body.error.code;
    }
    if (typeof body.error?.message === "string" && body.error.message.length > 0) {
      error.message = body.error.message;
    }
    if (body.error?.details?.relayFeeTopUp !== undefined) {
      error.relayFeeTopUp = body.error.details.relayFeeTopUp;
    }
    if (body.error?.details?.pendingClaim !== undefined) {
      error.pendingClaim = body.error.details.pendingClaim;
    }
  } catch {
    if (bodyText.length > 0) {
      error.message = bodyText;
    }
  }

  return error;
}
